import type { Data } from "@eai-toolkit/client";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { hasKey } from "@helpers/utils";

import type { LocalJob } from "../../modules/jobs/data/localTypes";
import type { TentativeJobSpec } from "../../modules/jobs/utils";
import {
  incrementJobName,
  tentativePayloadToJobSpec,
} from "../../modules/jobs/utils";
import { generateFriendlyName } from "../../utils/friendlyName";
import { getErrorMessage } from "../api/baseApi";
import { jobsApi } from "../api/jobsApi";
import type { AppDispatch, RootState } from "../store";

function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type FormSubmissionState<SubmitShape> =
  | {
      state: "READY_FOR_NEW_ENTRY";
      error: null;
      tentativePayload: SubmitShape;
      accountId: null;
      responseId: null;
    }
  | {
      state: "EDITING";
      error: null | string;
      tentativePayload: SubmitShape;
      accountId: string | null;
      responseId: null;
    }
  | {
      state: "SUBMITTING";
      error: null;
      tentativePayload: SubmitShape;
      accountId: string;
      responseId: null;
    }
  | {
      state: "STATUS_POLLING";
      error: null;
      tentativePayload: SubmitShape;
      accountId: string;
      responseId: string;
    }
  | {
      state: "ERROR_AFTER_SUBMIT";
      error: string;
      tentativePayload: SubmitShape;
      accountId: string;
      responseId: string;
    }
  | {
      state: "SUCCESS";
      error: null;
      tentativePayload: SubmitShape;
      accountId: string;
      responseId: string;
    };

export type NewJobFormState = FormSubmissionState<TentativeJobSpec>;

const defaultNewJobState: NewJobFormState = {
  state: "READY_FOR_NEW_ENTRY",
  error: null,
  tentativePayload: {
    randomlyGeneratedName: generateFriendlyName(),
    preemptable: true,
    restartable: true,
  },
  accountId: null,
  responseId: null,
};

export function validateNewJobFormState(
  formState: unknown,
): NewJobFormState | undefined {
  const state: NewJobFormState["state"] | undefined =
    hasKey(formState, "state") &&
    (formState.state === "READY_FOR_NEW_ENTRY" ||
      formState.state === "EDITING" ||
      formState.state === "SUBMITTING" ||
      formState.state === "STATUS_POLLING" ||
      formState.state === "ERROR_AFTER_SUBMIT" ||
      formState.state === "SUCCESS")
      ? formState.state
      : undefined;
  const error: NewJobFormState["error"] | undefined =
    hasKey(formState, "error") &&
    (formState.error === null || typeof formState.error === "string")
      ? formState.error
      : undefined;
  const tentativePayload: NewJobFormState["tentativePayload"] | undefined =
    hasKey(formState, "tentativePayload") &&
    formState.tentativePayload &&
    typeof formState.tentativePayload === "object"
      ? (formState.tentativePayload as TentativeJobSpec)
      : undefined;
  const accountId: NewJobFormState["accountId"] | undefined =
    hasKey(formState, "accountId") &&
    (formState.accountId === null || typeof formState.accountId === "string")
      ? formState.accountId
      : undefined;
  const responseId: NewJobFormState["responseId"] | undefined =
    hasKey(formState, "responseId") &&
    (formState.responseId === null || typeof formState.responseId === "string")
      ? formState.responseId
      : undefined;
  if (
    state !== undefined &&
    error !== undefined &&
    tentativePayload !== undefined &&
    accountId !== undefined &&
    responseId !== undefined
  ) {
    return {
      state,
      error,
      tentativePayload,
      accountId,
      responseId,
    } as NewJobFormState;
  }
  return undefined;
}

export const submitNewJob = createAsyncThunk<
  LocalJob,
  { dataObjectsMap: Map<string, Data> },
  { state: RootState; rejectValue: string; dispatch: AppDispatch }
>(
  "newJobForm/submitNewJob",
  async ({ dataObjectsMap }, { getState, rejectWithValue, dispatch }) => {
    const formState = getState().newJobForm;
    const { accountId, tentativePayload } = formState;

    if (!accountId) {
      return rejectWithValue("Account must be specified");
    }

    const jobSpecPost = tentativePayloadToJobSpec(
      tentativePayload,
      dataObjectsMap,
    );

    try {
      const response = await dispatch(
        jobsApi.endpoints.createJob.initiate({ accountId, jobSpecPost }),
      ).unwrap();
      return response;
    } catch (err: unknown) {
      const message =
        getErrorMessage(err) ??
        (err instanceof Error ? err.message : String(err));
      return rejectWithValue(message);
    }
  },
);

export const pollNewJobStatus = createAsyncThunk<
  LocalJob,
  { signal: AbortSignal },
  { state: RootState; rejectValue: string; dispatch: AppDispatch }
>(
  "newJobForm/pollNewJobStatus",
  async ({ signal }, { getState, rejectWithValue, dispatch }) => {
    const formState = getState().newJobForm;
    const { responseId } = formState;

    if (!responseId) {
      return rejectWithValue("Expected defined responseId");
    }

    // Helper to fetch job via RTK Query
    const fetchJob = async () => {
      const result = await dispatch(
        jobsApi.endpoints.getJobById.initiate(
          { jobId: responseId },
          { forceRefetch: true },
        ),
      ).unwrap();
      return result.data;
    };

    let updatedJob: LocalJob;
    try {
      updatedJob = await fetchJob();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return rejectWithValue(message);
    }
    if (signal.aborted) {
      return rejectWithValue("Polling cancelled");
    }

    let timeoutAmount = 2000;
    const maxTimeoutAmount = 15000;
    while (updatedJob.state === "QUEUED" || updatedJob.state === "QUEUING") {
      await timeout(timeoutAmount);
      if (signal.aborted) {
        return rejectWithValue("Polling cancelled");
      }
      timeoutAmount = Math.min(timeoutAmount * Math.SQRT2, maxTimeoutAmount);

      try {
        updatedJob = await fetchJob();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return rejectWithValue(message);
      }
      if (signal.aborted) {
        return rejectWithValue("Polling cancelled");
      }
    }

    if (updatedJob.state === "SUCCEEDED") {
      return updatedJob;
    } else if (updatedJob.state !== "RUNNING") {
      return rejectWithValue(
        updatedJob.stateInfo ||
          "Something unexpected happened inside of the job.",
      );
    }

    await timeout(10_000);
    if (signal.aborted) {
      return rejectWithValue("Polling cancelled");
    }

    try {
      updatedJob = await fetchJob();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return rejectWithValue(message);
    }
    if (signal.aborted) {
      return rejectWithValue("Polling cancelled");
    }

    if (updatedJob.state === "RUNNING" || updatedJob.state === "SUCCEEDED") {
      return updatedJob;
    }

    return rejectWithValue(
      updatedJob.stateInfo ||
        "Something unexpected happened inside of the job.",
    );
  },
);

const newJobFormSlice = createSlice({
  name: "newJobForm",
  initialState: defaultNewJobState as NewJobFormState,
  reducers: {
    editNewJobSpec: (
      state,
      action: PayloadAction<{
        update: Partial<{
          [Key in keyof TentativeJobSpec]: TentativeJobSpec[Key] | undefined;
        }>;
      }>,
    ): NewJobFormState => {
      if (state.state !== "READY_FOR_NEW_ENTRY" && state.state !== "EDITING") {
        console.error(
          `Invalid state machine action editNewJobSpec for state ${state.state}`,
        );
        return state;
      }
      return {
        state: "EDITING",
        error: state.state === "EDITING" ? state.error : null,
        tentativePayload: {
          ...state.tentativePayload,
          ...action.payload.update,
        },
        accountId: state.accountId,
        responseId: null,
      };
    },

    regenerateNewJobName: (state): NewJobFormState => {
      if (state.state !== "READY_FOR_NEW_ENTRY" && state.state !== "EDITING") {
        console.error(
          `Invalid state machine action regenerateNewJobName for state ${state.state}`,
        );
        return state;
      }
      return {
        state: "EDITING",
        error: state.state === "EDITING" ? state.error : null,
        tentativePayload: {
          ...state.tentativePayload,
          randomlyGeneratedName: generateFriendlyName(),
        },
        accountId: state.accountId,
        responseId: null,
      };
    },

    editNewJobAccount: (
      state,
      action: PayloadAction<{ accountId: string | null }>,
    ): NewJobFormState => {
      if (state.state !== "READY_FOR_NEW_ENTRY" && state.state !== "EDITING") {
        console.error(
          `Invalid state machine action editNewJobAccount for state ${state.state}`,
        );
        return state;
      }
      return {
        state: "EDITING",
        error: state.state === "EDITING" ? state.error : null,
        tentativePayload: state.tentativePayload,
        accountId: action.payload.accountId,
        responseId: null,
      };
    },

    swapNewJobFormState: (
      state,
      action: PayloadAction<{ formState: NewJobFormState }>,
    ) => {
      return action.payload.formState;
    },

    resetNewJobForm: () => {
      return {
        ...defaultNewJobState,
        tentativePayload: {
          ...defaultNewJobState.tentativePayload,
          randomlyGeneratedName: generateFriendlyName(),
        },
      };
    },

    reviseNewJobSpecAfterFailure: (state): NewJobFormState => {
      if (state.state !== "ERROR_AFTER_SUBMIT") {
        console.error(
          `Invalid state machine action reviseNewJobSpecAfterFailure for state ${state.state}`,
        );
        return state;
      }
      return {
        state: "EDITING",
        error: null,
        tentativePayload: {
          ...state.tentativePayload,
          randomlyGeneratedName: generateFriendlyName(),
          ...(state.tentativePayload.name
            ? { name: incrementJobName(state.tentativePayload.name) }
            : {}),
        },
        accountId: state.accountId,
        responseId: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitNewJob.pending, (state): NewJobFormState => {
        if (state.state !== "EDITING") {
          console.error(
            `Invalid state for submitNewJob.pending: ${state.state}`,
          );
          return state as NewJobFormState;
        }
        if (!state.accountId) {
          console.error("Cannot submit job without account.");
          return state as NewJobFormState;
        }
        return {
          state: "SUBMITTING",
          error: null,
          tentativePayload: state.tentativePayload,
          accountId: state.accountId,
          responseId: null,
        };
      })
      .addCase(submitNewJob.fulfilled, (state, action): NewJobFormState => {
        if (state.state !== "SUBMITTING") {
          return state as NewJobFormState;
        }
        return {
          state: "STATUS_POLLING",
          error: null,
          tentativePayload: state.tentativePayload,
          accountId: state.accountId,
          responseId: action.payload.id,
        };
      })
      .addCase(submitNewJob.rejected, (state, action): NewJobFormState => {
        if (state.state !== "SUBMITTING") {
          return state as NewJobFormState;
        }
        return {
          state: "EDITING",
          error: action.payload ?? action.error.message ?? "Unknown error",
          tentativePayload: state.tentativePayload,
          accountId: state.accountId,
          responseId: null,
        };
      })
      // pollNewJobStatus
      .addCase(pollNewJobStatus.fulfilled, (state): NewJobFormState => {
        if (state.state !== "STATUS_POLLING") {
          return state as NewJobFormState;
        }
        return {
          state: "SUCCESS",
          error: null,
          tentativePayload: state.tentativePayload,
          accountId: state.accountId,
          responseId: state.responseId,
        };
      })
      .addCase(pollNewJobStatus.rejected, (state, action): NewJobFormState => {
        if (state.state !== "STATUS_POLLING") {
          return state as NewJobFormState;
        }
        // Don't treat cancellation as an error
        if (action.payload === "Polling cancelled") {
          return state as NewJobFormState;
        }
        return {
          state: "ERROR_AFTER_SUBMIT",
          error: action.payload ?? action.error.message ?? "Unknown error",
          tentativePayload: state.tentativePayload,
          accountId: state.accountId,
          responseId: state.responseId,
        };
      });
  },
});

export const {
  editNewJobSpec,
  regenerateNewJobName,
  editNewJobAccount,
  swapNewJobFormState,
  resetNewJobForm,
  reviseNewJobSpecAfterFailure,
} = newJobFormSlice.actions;

export const selectNewJobForm = (state: RootState) => state.newJobForm;

export default newJobFormSlice.reducer;
